import React, { useState, useEffect, useCallback } from 'react';
import { exoneracionService } from 'services/exoneracionService';
import { matriculaService } from 'services/matriculaService';
import { catalogoService } from 'services/usuarioService';
import {
  ExoneracionResponse, ExoneracionPayload,
  TipoExoneracion, TIPO_EXONERACION_LABEL, TIPO_EXONERACION_COLOR,
} from 'types/exoneracion.types';
import { AñoEscolar } from 'types/usuario.types';
import { MatriculaResponse, CursoEstudianteItem } from 'types/matricula.types';
import { fmtAnioEscolar } from 'utils/anioEscolar';
import { catalogStorage } from 'utils/catalogStorage';

// ============================================================
// ExoneracionesPage
// Un estudiante queda liberado (total o parcialmente) de cursar
// un área/curso específico — ej. Educación Física por discapacidad.
// ============================================================

const SpinnerIcon = () => (
  <svg className="animate-spin w-5 h-5 text-blue-600" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/>
  </svg>
);

type Modo = 'estudiante' | 'curso';

const ExoneracionesPage: React.FC = () => {
  // ── Catálogos ──────────────────────────────────────────────
  const [anios, setAnios] = useState<AñoEscolar[]>([]);
  const [cursosCatalogo, setCursosCatalogo] = useState<{ idCurso: number; nombre: string }[]>([]);

  // ── Filtros de listado ─────────────────────────────────────
  const [modo, setModo]         = useState<Modo>('estudiante');
  const [anioId, setAnioId]     = useState<number | ''>('');
  const [dniLista, setDniLista] = useState('');
  const [cursoFiltro, setCursoFiltro] = useState<number | ''>('');

  // ── Datos ──────────────────────────────────────────────────
  const [items, setItems]   = useState<ExoneracionResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState<string | null>(null);

  // ── Modal nuevo ────────────────────────────────────────────
  const [modal, setModal]           = useState(false);
  const [confirmDel, setConfirmDel] = useState<ExoneracionResponse | null>(null);
  const [acting, setActing]         = useState(false);
  const [actError, setActError]     = useState<string | null>(null);

  // Form: búsqueda de matrícula
  const [busquedaDni, setBusquedaDni]   = useState('');
  const [buscando, setBuscando]         = useState(false);
  const [matricula, setMatricula]       = useState<MatriculaResponse | null>(null);
  const [cursosEstudiante, setCursosEstudiante] = useState<CursoEstudianteItem[]>([]);
  const [errorBusqueda, setErrorBusqueda] = useState<string | null>(null);

  // Form: campos de la exoneración
  const [formCursoId, setFormCursoId]     = useState<number | ''>('');
  const [formTipo, setFormTipo]           = useState<TipoExoneracion>('TOTAL');
  const [formMotivo, setFormMotivo]       = useState('');
  const [formDocRef, setFormDocRef]       = useState('');

  // ── Carga inicial ──────────────────────────────────────────
  useEffect(() => {
    const añosCached = catalogStorage.getAñosEscolares();
    const añoActivo  = catalogStorage.getAnioEscolarActivo();
    const años = añosCached ?? (añoActivo ? [añoActivo] : []);
    setAnios(años);
    if (años.length > 0) setAnioId(años[0].idAnioEscolar);

    const cursosCached = catalogStorage.getCursos();
    if (cursosCached) {
      setCursosCatalogo(cursosCached.map(c => ({ idCurso: c.idCurso, nombre: c.nombre })));
    } else {
      catalogoService.getCursos().then(cs =>
        setCursosCatalogo(cs.map(c => ({ idCurso: c.idCurso, nombre: c.nombre })))
      ).catch(() => {});
    }
  }, []);

  // ── Listado ────────────────────────────────────────────────
  const cargar = useCallback(async () => {
    if (modo === 'estudiante') {
      if (!dniLista.trim()) return;
      setLoading(true); setError(null);
      try {
        // buscar matrícula primero para obtener matriculaId
        const mat = await matriculaService.buscarPorDni(dniLista.trim(), anioId || undefined);
        if (!mat) { setError('No hay matrícula activa para ese DNI.'); setItems([]); return; }
        const data = await exoneracionService.listar({ matriculaId: mat.idMatricula });
        setItems(data);
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : 'Error al cargar');
      } finally { setLoading(false); }
    } else {
      if (!cursoFiltro || !anioId) return;
      setLoading(true); setError(null);
      try {
        const data = await exoneracionService.listar({ cursoId: cursoFiltro as number, anioEscolarId: anioId as number });
        setItems(data);
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : 'Error al cargar');
      } finally { setLoading(false); }
    }
  }, [modo, dniLista, cursoFiltro, anioId]);

  // ── Búsqueda de matrícula en el modal ─────────────────────
  const buscarMatricula = async () => {
    if (!busquedaDni.trim() || !anioId) return;
    setBuscando(true); setErrorBusqueda(null); setMatricula(null); setCursosEstudiante([]);
    setFormCursoId('');
    try {
      const found = await matriculaService.buscarPorDni(busquedaDni.trim(), anioId as number);
      if (!found) {
        setErrorBusqueda('No se encontró matrícula activa para ese DNI en el año seleccionado.');
      } else {
        setMatricula(found);
        const resp = await matriculaService.getCursosByEstudiante(found.estudianteId, found.anioEscolarId);
        setCursosEstudiante(resp.cursos ?? []);
      }
    } catch (e: unknown) {
      setErrorBusqueda(e instanceof Error ? e.message : 'Error al buscar');
    } finally { setBuscando(false); }
  };

  // ── Guardar nueva exoneración ──────────────────────────────
  const handleGuardar = async () => {
    if (!matricula || !formCursoId) return;
    setActing(true); setActError(null);
    try {
      const payload: ExoneracionPayload = {
        matriculaId: matricula.idMatricula,
        cursoId:     formCursoId as number,
        tipo:        formTipo,
        motivo:      formMotivo.trim() || undefined,
        documentoReferencia: formDocRef.trim() || undefined,
      };
      const created = await exoneracionService.crear(payload);
      setItems(prev => [created, ...prev]);
      setModal(false);
    } catch (e: unknown) {
      setActError(e instanceof Error ? e.message : 'Error al registrar');
    } finally { setActing(false); }
  };

  // ── Eliminar (soft delete) ─────────────────────────────────
  const handleEliminar = async (ex: ExoneracionResponse) => {
    setActing(true); setActError(null);
    try {
      await exoneracionService.eliminar(ex.idExoneracion);
      setItems(prev => prev.map(i => i.idExoneracion === ex.idExoneracion ? { ...i, activo: false } : i));
      setConfirmDel(null);
    } catch (e: unknown) {
      setActError(e instanceof Error ? e.message : 'Error al eliminar');
    } finally { setActing(false); }
  };

  const abrirModal = () => {
    setModal(true);
    setBusquedaDni(''); setMatricula(null); setCursosEstudiante([]); setErrorBusqueda(null);
    setFormCursoId(''); setFormTipo('TOTAL'); setFormMotivo(''); setFormDocRef('');
    setActError(null);
  };

  const puedeGuardar = !!matricula && !!formCursoId;

  return (
    <div className="space-y-5">
      {/* Cabecera */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-700 rounded-2xl p-6 text-white">
        <h1 className="text-2xl font-bold">Exoneraciones de Curso</h1>
        <p className="text-blue-200 mt-1">
          Estudiantes liberados total o parcialmente de cursar un área curricular específica
        </p>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 space-y-4">
        {/* Selector de modo */}
        <div className="flex gap-2">
          {(['estudiante', 'curso'] as Modo[]).map(m => (
            <button
              key={m}
              onClick={() => { setModo(m); setItems([]); setError(null); }}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                modo === m
                  ? 'bg-blue-700 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {m === 'estudiante' ? 'Por estudiante' : 'Por curso'}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-end gap-4">
          {/* Año siempre visible */}
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Año Escolar</label>
            <select
              value={anioId}
              onChange={e => setAnioId(e.target.value ? Number(e.target.value) : '')}
              className="px-3 py-2.5 rounded-lg border border-slate-200 text-sm bg-slate-50 outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Seleccionar</option>
              {anios.map(a => <option key={a.idAnioEscolar} value={a.idAnioEscolar}>{fmtAnioEscolar(a)}</option>)}
            </select>
          </div>

          {modo === 'estudiante' ? (
            <>
              <div className="flex-1 min-w-48">
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">DNI del estudiante</label>
                <input
                  type="text" maxLength={8}
                  value={dniLista}
                  onChange={e => setDniLista(e.target.value)}
                  placeholder="12345678"
                  className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm bg-slate-50 outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <button
                onClick={cargar}
                disabled={!dniLista.trim() || loading}
                className="flex items-center gap-2 bg-slate-700 hover:bg-slate-800 text-white text-sm font-semibold rounded-xl px-4 py-2.5 transition-colors disabled:opacity-60"
              >
                {loading ? <SpinnerIcon /> : 'Buscar'}
              </button>
            </>
          ) : (
            <>
              <div className="flex-1 min-w-48">
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Curso</label>
                <select
                  value={cursoFiltro}
                  onChange={e => setCursoFiltro(e.target.value ? Number(e.target.value) : '')}
                  className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm bg-slate-50 outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Seleccionar curso</option>
                  {cursosCatalogo.map(c => <option key={c.idCurso} value={c.idCurso}>{c.nombre}</option>)}
                </select>
              </div>
              <button
                onClick={cargar}
                disabled={!cursoFiltro || !anioId || loading}
                className="flex items-center gap-2 bg-slate-700 hover:bg-slate-800 text-white text-sm font-semibold rounded-xl px-4 py-2.5 transition-colors disabled:opacity-60"
              >
                {loading ? <SpinnerIcon /> : 'Buscar'}
              </button>
            </>
          )}

          <button
            onClick={abrirModal}
            disabled={!anioId}
            className="flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white text-sm font-bold rounded-xl px-5 py-2.5 transition-colors disabled:opacity-60"
          >
            + Nueva Exoneración
          </button>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-600 text-sm">{error}</div>}

      {/* Tabla */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100">
          <h2 className="font-bold text-slate-800">Exoneraciones registradas</h2>
          <p className="text-slate-400 text-xs mt-0.5">{items.length} registros</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><SpinnerIcon /></div>
        ) : items.length === 0 ? (
          <div className="py-14 text-center text-slate-400 text-sm">
            Usa los filtros de arriba para buscar exoneraciones
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr>
                  {['Estudiante', 'DNI', 'Grado / Sección', 'Curso', 'Tipo', 'Motivo', 'Doc. referencia', 'Estado', 'Acciones'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map(ex => (
                  <tr key={ex.idExoneracion} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3">
                      <p className="text-sm font-semibold text-slate-800">{ex.estudianteApellidos}, {ex.estudianteNombres}</p>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-500">{ex.estudianteDni}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {ex.gradoNombre} — {ex.seccionNombre}
                      <span className="ml-1.5 text-xs text-slate-400">{ex.gradoNivel}</span>
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-slate-700">{ex.cursoNombre}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${TIPO_EXONERACION_COLOR[ex.tipo]}`}>
                        {TIPO_EXONERACION_LABEL[ex.tipo]}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600 max-w-[180px] truncate" title={ex.motivo ?? ''}>
                      {ex.motivo ?? <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-500">
                      {ex.documentoReferencia ?? <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${ex.activo ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                        {ex.activo ? 'Activa' : 'Inactiva'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {ex.activo && (
                        <button
                          onClick={() => setConfirmDel(ex)}
                          className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-50 text-red-700 hover:bg-red-100 border border-red-200 transition-colors"
                        >
                          Desactivar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal nueva exoneración */}
      {modal && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden">
            <div className="bg-gradient-to-r from-blue-900 to-blue-700 px-6 py-4">
              <h3 className="text-white font-bold">Nueva Exoneración</h3>
              <p className="text-blue-200 text-xs mt-0.5">
                Libera a un estudiante de cursar un área curricular específica
              </p>
            </div>

            <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              {/* Búsqueda estudiante */}
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">
                  DNI del estudiante *
                </label>
                <div className="flex gap-2">
                  <input
                    type="text" maxLength={8}
                    value={busquedaDni}
                    onChange={e => { setBusquedaDni(e.target.value); setMatricula(null); setCursosEstudiante([]); setErrorBusqueda(null); setFormCursoId(''); }}
                    placeholder="12345678"
                    className="flex-1 px-3 py-2.5 rounded-lg border border-slate-200 text-sm bg-slate-50 outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    onClick={buscarMatricula}
                    disabled={buscando || !busquedaDni.trim() || !anioId}
                    className="flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-blue-700 hover:bg-blue-800 text-white text-sm font-semibold disabled:opacity-60 transition-colors"
                  >
                    {buscando ? <SpinnerIcon /> : 'Buscar'}
                  </button>
                </div>
                {errorBusqueda && <p className="text-red-500 text-xs mt-1">{errorBusqueda}</p>}
                {matricula && (
                  <div className="mt-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-sm text-green-800">
                    ✓ {matricula.estudianteApellidos}, {matricula.estudianteNombres}
                    <span className="ml-2 text-green-600">{matricula.gradoNombre} {matricula.seccionNombre}</span>
                  </div>
                )}
              </div>

              {/* Curso */}
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Curso *</label>
                <select
                  value={formCursoId}
                  onChange={e => setFormCursoId(e.target.value ? Number(e.target.value) : '')}
                  disabled={cursosEstudiante.length === 0}
                  className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm bg-slate-50 outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  <option value="">{matricula ? 'Seleccionar curso' : 'Busca un estudiante primero'}</option>
                  {cursosEstudiante.map(c => (
                    <option key={c.cursoId} value={c.cursoId}>{c.cursoNombre}</option>
                  ))}
                </select>
              </div>

              {/* Tipo */}
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Tipo *</label>
                <div className="flex gap-3">
                  {(['TOTAL', 'PARCIAL'] as TipoExoneracion[]).map(t => (
                    <label key={t} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="tipo"
                        value={t}
                        checked={formTipo === t}
                        onChange={() => setFormTipo(t)}
                        className="accent-blue-700"
                      />
                      <span className="text-sm font-medium text-slate-700">{TIPO_EXONERACION_LABEL[t]}</span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-slate-400 mt-1">
                  {formTipo === 'TOTAL' ? 'El estudiante no cursa ni es evaluado en este curso.' : 'El estudiante cursa el curso con adaptaciones curriculares.'}
                </p>
              </div>

              {/* Motivo */}
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Motivo (opcional)</label>
                <textarea
                  rows={2}
                  value={formMotivo}
                  onChange={e => setFormMotivo(e.target.value)}
                  placeholder="Ej: Discapacidad motriz según informe médico adjunto"
                  className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm bg-slate-50 outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>

              {/* Documento de referencia */}
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Documento de referencia (opcional)</label>
                <input
                  type="text"
                  value={formDocRef}
                  onChange={e => setFormDocRef(e.target.value)}
                  placeholder="Ej: INFORME-MED-2026-045"
                  className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm bg-slate-50 outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {actError && (
                <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2.5 text-red-600 text-sm">{actError}</div>
              )}
            </div>

            <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-between">
              <button
                onClick={() => setModal(false)}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-200 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleGuardar}
                disabled={acting || !puedeGuardar}
                className="flex items-center gap-2 px-5 py-2 rounded-lg bg-blue-700 hover:bg-blue-800 text-white text-sm font-bold transition-colors disabled:opacity-60"
              >
                {acting && <SpinnerIcon />}
                Registrar Exoneración
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmar desactivación */}
      {confirmDel && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full">
            <h3 className="font-bold text-lg text-slate-800 mb-2">¿Desactivar exoneración?</h3>
            <p className="text-slate-600 text-sm mb-1">
              {confirmDel.estudianteApellidos}, {confirmDel.estudianteNombres}
            </p>
            <p className="text-slate-500 text-sm mb-1">Curso: <b>{confirmDel.cursoNombre}</b></p>
            <p className="text-slate-400 text-xs mb-5">El historial se conserva (soft delete).</p>
            {actError && <p className="text-red-500 text-sm mb-3">{actError}</p>}
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setConfirmDel(null)}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-100 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleEliminar(confirmDel)}
                disabled={acting}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold bg-red-600 hover:bg-red-700 text-white transition-colors disabled:opacity-60"
              >
                {acting && <SpinnerIcon />}
                Desactivar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExoneracionesPage;
